import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const DEFAULT_WORKSPACE_ID = "ws_7f2a9c4e1d68";
const LEGACY_DEFAULT_WORKSPACE_ID = "ws_default";

const WorkspaceContext = createContext(null);

export const WorkspaceProvider = ({ children }) => {
  const { user, setUser } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const storedInitialId = localStorage.getItem("workspace_id");
  const initialId = storedInitialId === LEGACY_DEFAULT_WORKSPACE_ID
    ? DEFAULT_WORKSPACE_ID
    : (storedInitialId || DEFAULT_WORKSPACE_ID);
  const [currentId, setCurrentId] = useState(initialId);
  const currentIdRef = useRef(initialId);
  const lastUserIdRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get("/workspaces");
      setWorkspaces(res.data);
      const preferred = user?.preferences?.default_workspace_id;
      const storedValue = localStorage.getItem("workspace_id");
      const stored = storedValue === LEGACY_DEFAULT_WORKSPACE_ID
        ? DEFAULT_WORKSPACE_ID
        : (storedValue || DEFAULT_WORKSPACE_ID);
      if (storedValue === LEGACY_DEFAULT_WORKSPACE_ID) {
        localStorage.setItem("workspace_id", DEFAULT_WORKSPACE_ID);
      }
      const preferredIsAccessible = Boolean(preferred && res.data.find((w) => w.id === preferred));
      const currentIsAccessible = Boolean(res.data.find((w) => w.id === currentIdRef.current));
      const storedIsAccessible = Boolean(res.data.find((w) => w.id === stored));
      const userChanged = lastUserIdRef.current !== user?.user_id;
      lastUserIdRef.current = user?.user_id;

      // Apply a saved preference when entering a user's session. During an
      // active session, preserve the user's current workspace unless it is no
      // longer accessible.
      const selected = (userChanged && preferredIsAccessible)
        ? preferred
        : currentIsAccessible
          ? currentIdRef.current
          : preferredIsAccessible
            ? preferred
            : storedIsAccessible
              ? stored
              : res.data[0]?.id;
      if (selected && selected !== currentIdRef.current) {
        localStorage.setItem("workspace_id", selected);
        currentIdRef.current = selected;
        setCurrentId(selected);
      }
      setLoaded(true);
    } catch {
      // ignore - not authenticated yet
    }
  }, [user]);

  useEffect(() => {
    if (user) refresh();
  }, [user, refresh]);

  const switchWorkspace = (id) => {
    localStorage.setItem("workspace_id", id);
    currentIdRef.current = id;
    setCurrentId(id);
  };

  const setDefaultWorkspace = async (id) => {
    const res = await api.put("/auth/me/default-workspace", { workspace_id: id || null });
    setUser(res.data);
    return res.data;
  };

  const createWorkspace = async (name, logo = null, makeDefault = false) => {
    const res = await api.post("/workspaces", { name, logo });
    await refresh();
    switchWorkspace(res.data.id);
    if (makeDefault) await setDefaultWorkspace(res.data.id);
    return res.data;
  };

  const updateWorkspace = async (id, name, logo = null, makeDefault) => {
    const res = await api.put(`/workspaces/${id}`, { name, logo });
    await refresh();
    if (typeof makeDefault === "boolean") {
      await setDefaultWorkspace(makeDefault ? id : null);
    }
    return res.data;
  };

  const current = workspaces.find((w) => w.id === currentId) || { id: currentId, name: "ATH1" };
  const defaultWorkspaceId = user?.preferences?.default_workspace_id || null;

  return (
    <WorkspaceContext.Provider value={{
      workspaces, current, loaded, defaultWorkspaceId, switchWorkspace,
      setDefaultWorkspace, createWorkspace, updateWorkspace, refresh,
    }}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => useContext(WorkspaceContext);
